import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useThemeStore } from '@/stores/themeStore';
const options = [
    { value: 'light', label: 'Light', Icon: SunIcon },
    { value: 'dark', label: 'Dark', Icon: MoonIcon },
    { value: 'auto', label: 'Auto', Icon: ComputerDesktopIcon },
];
/**
 * Top-nav theme dropdown. Trigger is a 38px glass icon button; open state
 * reveals a Light / Dark / Auto menu that matches the `.theme-menu` styling
 * from the mockup (glass pill rows with royal-active highlight).
 *
 * Uses Headless UI for keyboard + ARIA.
 */
export function ThemeSwitcher() {
    const theme = useThemeStore((s) => s.theme);
    const setTheme = useThemeStore((s) => s.setTheme);
    // Trigger icon reflects the stored choice (seeing `auto` as its own glyph
    // is clearer than silently rendering sun or moon).
    const TriggerIcon = theme === 'light' ? SunIcon : theme === 'dark' ? MoonIcon : ComputerDesktopIcon;
    return (_jsxs(Menu, { as: "div", className: "relative", children: [_jsx(MenuButton, { className: "grid h-[38px] w-[38px] place-items-center rounded-full bg-[var(--glass-bg-strong)] border border-[var(--glass-border)] text-[var(--ink-700)] shadow-[inset_0_1px_0_rgba(255,255,255,0.5),var(--shadow-xs)] hover:text-[var(--ink-900)] hover:-translate-y-px hover:shadow-[var(--shadow-sm)] transition-[transform,box-shadow,color] cursor-pointer focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring)]", "aria-label": "Change theme", children: _jsx(TriggerIcon, { className: "h-[17px] w-[17px]" }) }), _jsx(MenuItems
            // Render inline (absolute) rather than via Headless UI's `anchor`
            // floating portal. The portal lives detached at <body>; when picking a
            // theme re-renders this switcher in place, the portal could be left
            // capturing the page until the next click ("stuck" screen). Inline
            // positioning keeps the menu in the normal DOM and removes that.
            // (QA: Raji #6)
            , { 
                // Render inline (absolute) rather than via Headless UI's `anchor`
                // floating portal. The portal lives detached at <body>; when picking a
                // theme re-renders this switcher in place, the portal could be left
                // capturing the page until the next click ("stuck" screen). Inline
                // positioning keeps the menu in the normal DOM and removes that.
                // (QA: Raji #6)
                className: "absolute right-0 top-full mt-2 min-w-[160px] rounded-[var(--r-lg)] border border-[var(--line-strong)] p-1.5 shadow-[var(--shadow-md)] z-[60] focus:outline-none", style: {
                    // QA Raji #13: glass-bg-strong is only ~9% opaque in dark mode, so the
                    // page showed through the panel. Use the solid elevated surface
                    // (matches the Search palette, profile menu, and notifications panel).
                    background: 'var(--canvas-2)',
                    backdropFilter: 'blur(22px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(22px) saturate(160%)',
                }, children: options.map(({ value, label, Icon }) => (_jsx(MenuItem, { children: ({ focus }) => (_jsxs("button", { type: "button", onClick: () => {
                            // Theme applies instantly; applyTheme() suppresses transitions
                            // during the swap so there is no backdrop-blur freeze. (Raji #6/#15)
                            setTheme(value);
                        }, className: clsx('flex w-full items-center gap-2.5 rounded-full px-3 py-2.5 text-[13.5px] font-medium text-[var(--ink-800)] cursor-pointer text-left transition-colors', focus && 'bg-[rgba(11,13,35,0.05)] text-[var(--ink-900)]', theme === value &&
                            'theme-opt-active bg-[linear-gradient(180deg,var(--lav-50),var(--white))] text-[var(--royal)]'), children: [_jsx(Icon, { className: "h-[15px] w-[15px] opacity-80" }), label] })) }, value))) })] }));
}
